import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'

/**
 * Designed to be used in a child render function of a Formik Field
 * A multi select input field composed of tiles in a grid that highlight when selected
 * options should be an array of objects that each have the shape { value, label, icon }
 * where 'icon' is optional, label is what's displayed, and value is what's put into the array
 * for the formik field name.
 *
 * @param {Object} param0
 */
const TileSelectList = ({ onChange, options = [], resetSelectedValuesTo, allowOtherOption = false }) => {
  const startingState = useMemo(() => resetSelectedValuesTo ?? [], [resetSelectedValuesTo])
  const [selected, setSelected] = useState(startingState)
  const [otherTileSelected, setOtherTileSelected] = useState(false)
  const [otherInputValue, setOtherInputValue] = useState(null)
  const otherRef = useRef({ current: { value: '' } })

  useEffect(() => {
    if (otherInputValue !== null) {
      // if the input in the "other" field is valid, concatenate it to the selected options
      const values = new Set([...selected, otherInputValue])
      const cleanedValues = Array.from(values).map((val) => val.trim())
      onChange(cleanedValues)
    } else {
      // otherwise, just pass the current selected array back
      onChange(selected)
    }
  }, [selected, otherInputValue])

  // hook to reset the values of this component when new resetSelectedValuesTo are passed
  // allows the state of the component to be changed by outside/adjacent components.
  useEffect(() => {
    setSelected(startingState)
    setOtherTileSelected(false)
    setOtherInputValue(null)
  }, [startingState])

  // select or deselect the tile value based on whether or not it's already selected
  const handleRegularTileClick = useCallback(
    (value) => {
      const searchIndex = selected.findIndex((el) => el === value)
      if (searchIndex === -1) {
        setSelected([...selected, value])
      } else {
        let tempSelected = [...selected]
        tempSelected.splice(searchIndex, 1)
        setSelected(tempSelected)
      }
    },
    [selected]
  )

  const handleOtherTileClick = useCallback(() => {
    // toggle the other tile selected status and clear the other input field value
    // set the input value to null when deselecting and initialize the input value to '' when selecting
    setOtherInputValue(otherTileSelected ? null : '')
    setOtherTileSelected(!otherTileSelected)
  }, [otherTileSelected])

  return (
    <>
      <div className='wrapper'>
        <div className='tileContainer'>
          {options.map(({ value, label, icon }, index) => (
            <div
              className={`tile ${selected.includes(value) ? 'active' : ''}`}
              key={index}
              onClick={() => handleRegularTileClick(value)}
            >
              {icon && icon({ height: '24px', width: 'auto', fill: selected.includes(value) ? '#fff' : '#676767' })}
              <h3 className='tileLabel'>{label}</h3>
            </div>
          ))}
          {allowOtherOption && (
            <div className={`tile ${otherTileSelected ? 'active' : ''}`} onClick={handleOtherTileClick}>
              <h3 className='tileLabel'>Other</h3>
            </div>
          )}
        </div>
        {otherTileSelected && (
          <input
            ref={otherRef}
            className='sportsBook__formField'
            placeholder='Other type here'
            onChange={() => setOtherInputValue(otherRef?.current?.value)}
          />
        )}
      </div>
      <style jsx>{`
        .container {
          max-width: 335px;
          text-align: center;
          justify-content: center;
        }
        .tileContainer {
          display: flex;
          flex-wrap: wrap;
          text-align: left;
          justify-content: left;
        }
        .tile {
          flex-basis: 30%;
          min-height: 94px;
          justify-content: center;
          background: #f1f2f2;
          border-radius: 4px;
          text-align: center;
          margin: 0.25rem;
          padding: 1.25rem 0;
          color: #676767;
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
        }
        .active {
          background-color: #1555d0;
          color: #fff;
        }
        .active svg {
          fill: #fff;
        }
        .tileLabel {
          font-family: 'IBM Plex Sans Condensed';
          font-size: 14px;
          font-weight: bold;
          letter-spacing: 0;
          line-height: 21px;
          text-align: center;
          margin: 0;
        }
        input {
          margin: 1rem auto;
          width: 100%;
        }
      `}</style>
    </>
  )
}

export default TileSelectList
